import { processLinkUrl } from "@/lib/formatters/formatLinks"
import { localizeUrl } from "@/lib/url/localizeUrl"

export function safeSlug(value: unknown): string {
  if (typeof value === "string") return value
  if (value == null) return ""
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>
    if ("en-US" in obj && typeof obj["en-US"] === "string") return obj["en-US"]
  }
  return ""
}

/**
 * Normalizes layout `theme` field values to `t2-theme-*` classes from `terra.css`.
 * Maps Contentful `t2-preset-theme-*` (e.g. pricing presets) to the equivalent `t2-theme-*`.
 */
export function formatLayoutThemeToT2ThemeClass(
  raw: string | undefined | null,
): string | undefined {
  if (raw == null || typeof raw !== "string") return undefined
  const t = raw.trim()
  if (!t) return undefined
  if (t.startsWith("t2-preset-theme-")) {
    return `t2-theme-${t.slice("t2-preset-theme-".length)}`
  }
  if (t.startsWith("t2-theme-")) {
    return t
  }
  return normalToMachineTheme(t) as string
}

export const normalToMachineTheme = (presetTheme: string): any => {
  if (presetTheme?.toString()?.startsWith("t2-")) {
    // if theme already formatted, return as is
    return presetTheme
  }
  let machineTheme = `t2-theme-one`
  if (presetTheme == "Gray" || presetTheme == "Concrete Gray") {
    machineTheme = "t2-theme-two"
  } else if (presetTheme == "Black" || presetTheme == "Trimble Dark Gray") {
    machineTheme = "t2-theme-three"
  } else if (presetTheme == "Blue" || presetTheme == "Trimble Blue") {
    machineTheme = "t2-theme-four"
  } else if (presetTheme == "Dark Blue" || presetTheme == "Dark Navy") {
    machineTheme = "t2-theme-five"
  } else if (presetTheme == "Textured Light") {
    machineTheme = "t2-theme-six"
  } else if (presetTheme == "Textured Dark") {
    machineTheme = "t2-theme-seven"
  }
  return machineTheme
}

export const normalToMachineActionTheme = (presetTheme: string): any => {
  let machineTheme = `t2-action-theme-one`
  if (presetTheme == "Action Black") {
    machineTheme = "t2-action-theme-two"
  } else if (presetTheme == "Action White") {
    machineTheme = "t2-action-theme-three"
  }
  return machineTheme
}

export const colorSettingDetection = (presetTheme: string): any => {
  if (presetTheme == "") {
    return `t2-color-setting--light`
  } // default
  const colorSetting =
    presetTheme == "t2-action-theme-two" ||
    presetTheme == "t2-theme-three" ||
    presetTheme == "t2-theme-four" ||
    presetTheme == "t2-theme-five" ||
    presetTheme == "t2-theme-seven"
      ? `t2-color-setting--dark`
      : `t2-color-setting--light`
  return colorSetting
}

export const truncateText = (text: string, length?: number) => {
  if (!text || !length) {
    return text
  }
  return text.length > length ? text.slice(0, length) + "..." : text
}

function resolveEntryRefPath(ref: any): string {
  if (ref.__typename === "ProductDetailPage") {
    // `slug` is the source of truth for this page's URL — no link without one.
    return ref.slug ? `/products/${ref.slug}/buy` : ""
  }
  if (ref.__typename === "ProductTrialPage") {
    // `slug` is the source of truth for this page's URL — no link without one.
    return ref.slug ? `/products/${ref.slug}/trial` : ""
  }
  if (ref.__typename === "PageProductPage" && ref.slug) {
    return `/products/${safeSlug(ref.slug)}`
  }
  if (ref.slug) {
    return `/${safeSlug(ref.slug)}`
  }
  return ""
}

function resolveLinkTarget(target: any, lang?: string): string | null {
  if (!target) return null
  if (lang) {
    const result = processLinkUrl(target, lang)
    if (typeof result === "string") return result
    const path = resolveEntryRefPath(target)
    return path ? localizeUrl(path, lang) : null
  }
  if (target.__typename === "ExternalLink") return target?.url || null
  const path = resolveEntryRefPath(target)
  return path || null
}

function resolveSimpleLinkDestination(
  buttonData: any,
  lang?: string,
): string | null {
  if (buttonData.dynamicPageReference) {
    return resolveLinkTarget(buttonData.dynamicPageReference, lang)
  }
  if (buttonData.urlString) {
    if (buttonData.urlString.startsWith("#")) return buttonData.urlString
    if (lang) {
      const result = processLinkUrl(buttonData.urlString, lang)
      return typeof result === "string" ? result : String(buttonData.urlString)
    }
    return buttonData.urlString
  }
  return null
}

// Resolves a PDP link field (`link` or `simpleLink` content type) to `{ url, text }`. 
export function resolvePdpLinkEntry(
  entry: any,
  lang?: string,
): { url?: string | null; text?: string | null } | null {
  if (!entry) return null

  if (entry.__typename === "SimpleLink") {
    const url =
      resolveSimpleLinkDestination(entry, lang) ?? entry.linkAsset?.url ?? null
    const text = entry.linkText ?? null
    return url && text ? { url, text } : url ? { url, text: text ?? url } : null
  }

  const target = entry.url
  if (!target) return null

  const url = resolveLinkTarget(target, lang) ?? target?.url ?? null
  const text = target?.text ?? entry.text ?? entry.title ?? null
  return url && text ? { url, text } : url ? { url, text: text ?? url } : null
}

export const handleButtons = (
  items: any,
  includeType: boolean = true,
  lang?: string,
): any => {
  const buttons: any[] = []
  const filtered_items = items?.filter(function (el: any) {
    return el != null
  })
  filtered_items?.map((buttonData: any) => {
    if (buttonData?.__typename == "Link") {
      const label = buttonData.textLabel || buttonData.text
      if (label) {
        const target =
          typeof buttonData.url === "string"
            ? buttonData
            : buttonData.url || buttonData.destination
        const destination =
          resolveLinkTarget(target, lang) ||
          (typeof buttonData.url === "string" && buttonData.url
            ? buttonData.url
            : null) ||
          "#never-doing"
        buttons.push({
          id: buttonData?.sys.id,
          primary: buttonData.isPrimary,
          label,
          destination,
        })
      }
    } else if (buttonData?.__typename == "SimpleLink") {
      /* 
                Example Primary urlstring
                {
                    __typename: 'SimpleLink',
                    sys: { id: '3YmS2iNNfRY9mEcNPxHNxk' },
                    internalTitle: 'MX Runbook Primary Button A',
                    linkText: 'Primary A',
                    urlString: '/something',
                    dynamicPageReference: null,
                    ariaLabel: 'Read more about this runbook',
                    isPrimary: true,
                    linkAsset: null
                }
                Example Primary reference
                {
                    sys: { id: '5dYeBlRfZhFhoFIqXfa3I5' },
                    internalTitle: 'MX Runbook Primary Button B',
                    linkText: 'Primary B',
                    urlString: null,
                    ariaLabel: 'Learn More',
                    isPrimary: true,
                    dynamicPageReference: { slug: 'mx-runbook' },
                    linkAsset: null
                }
                Example Primary pdf
                {
                    __typename: 'SimpleLink',
                    sys: { id: '3YmS2iNNfRY9mEcNPxHNxk' },
                    internalTitle: 'MX Runbook Primary Button A',
                    linkText: 'Primary A',
                    urlString: null,
                    dynamicPageReference: null,
                    ariaLabel: 'Read more about this runbook',
                    isPrimary: true
                    linkAsset: {
                        __typename: 'Asset',
                        sys: { id: '57PgP3xzaHsDVyXdKUjSUe' },
                        title: 'Check It Out!',
                        url: 'https://assets.ctfassets.net/citn2sn5tdjr/57PgP3xzaHsDVyXdKUjSUe/fa735bb65cdbf65a394e23735ef65cb7/Customer-Portal-5-Hidden-Gems.pdf',
                        description: '',
                        fileName: 'Customer-Portal-5-Hidden-Gems.pdf',
                        contentType: 'application/pdf'
                    }
                }
            */

      const destination = resolveSimpleLinkDestination(buttonData, lang)
      if (destination && buttonData.linkText) {
        buttons.push({
          id: buttonData?.sys.id,
          primary: buttonData.isPrimary,
          label: buttonData.linkText,
          destination,
        })
      } else if (buttonData.linkAsset) {
        if (buttonData.linkText) {
          buttons.push({
            id: buttonData?.sys.id,
            primary: buttonData.isPrimary,
            label: buttonData.linkText,
            destination: buttonData.linkAsset.url,
            downloadName: buttonData.linkAsset.fileName,
          })
        }
      }
    }
  })
  // if includeType is false, return a simplified version of the buttons without primary boolean
  if (includeType == false) {
    return buttons.map(button => {
      return {
        label: button.label,
        destination: button.destination,
      }
    })
  }

  const returnableButtons = buttons.length > 0 ? buttons : null
  return returnableButtons
}

export function isCenterAll(tags: string[] | undefined | null): boolean {
  return Array.isArray(tags) && tags.includes("centerAll")
}

// returns true if this entry has the needed Contentful tags
export async function hasContenfulTags(
  entry: any,
  neededTags: string[],
): Promise<any> {
  if (entry && entry?.contentfulMetadata?.tags) {
    for (const tag of entry.contentfulMetadata.tags) {
      if (neededTags && neededTags.includes(tag.id)) {
        return true
      }
    }
  }
  return false
}
